import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject } from 'rxjs';
import { NavService } from '../../../../layout/nav/nav.service';
import { DataTableColumn } from '../../../../shared/components/data-table/DataTableColumn.model';
import { SortParams, SortOrderType } from '../../../../shared/search/SearchParams';
import { PillComponent } from '../../../../shared/components/pill/pill.component';
import { DataTableFilter } from '../../../../shared/components/data-table/filters/components/DataTableFilter';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { ActiveEventMessage, DataTableFilterMessageService } from '../../../../shared/components/data-table/filters/components/data-table-filter-message-service';
import { SearchService } from '../../../../shared/search/search.service';

@Component({
  selector: 'app-deleted-claims-table',
  templateUrl: './deleted-claims-table.component.html',
  styleUrls: ['./deleted-claims-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    DataTableFilterPillComponent,
    MatSortModule,
    MatPaginator,
    MatTableModule,
    PillComponent,
    MatProgressSpinner,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconButton
  ]
})
export class DeletedClaimsTableComponent implements OnInit {

  public rows = new MatTableDataSource<any>();
  public columns = [];

  @Input() component!: string;
  @Input() set dataSource(value: MatTableDataSource<any>) {
    this._dataSource = value;
    this.rows = value;
  };

  @Input() displayedColumns!: DataTableColumn[];
  @Input() tableLength?: number;
  @Input() pageSize?: number;
  @Input() disablePageSizeSelector?: boolean = false;
  @Input() searchTimeoutMs?: number = 500;
  @Input() filterDefinitions!: DataTableFilter[];
  @Input() displayLoadingIndicator!: BehaviorSubject<boolean>;
  @Input() actionButtonText?: string;
  @Input() getActionButtonColor?: (column: any) => string;
  @Input() showSearch?: boolean = true;
  @Input() navigateOnRowClick?: boolean = true;
  @Input() showPaginator: boolean = true;
  @Input() hidePointer: boolean = false;
  @Input() disableSorting: boolean = false;
  @Input() getStatusColor?: (status: string) => string;
  @Input() rowClicked?: (row: any) => void;

  @Input() initialPageSize?: BehaviorSubject<number>;
  @Input() initialSort?: BehaviorSubject<Sort | undefined>;
  @Input() initialKeyword?: BehaviorSubject<string>;

  @Output() pageEvent: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() searchEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() sortEvent: EventEmitter<SortParams> = new EventEmitter<SortParams>();
  @Output() actionClickedEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChildren('filters') filters!: QueryList<ViewContainerRef>;
  @ViewChild('searchKeyword') searchKeywordInput!: ElementRef;

  _dataSource!: MatTableDataSource<any>;
  displayedColumnNames: string[] = [];
  searchTimeout: any;
  pageNumber: number = 1;
  searchKeyword: string = '';
  filterPills = new Map<string, DataTableFilterPillState>();
  selectedRowIndex: any;
  selectedOrganizationName!: string;

  constructor(
    private router: Router,
    private filterMessageService: DataTableFilterMessageService,
    private navService: NavService,
    private searchService: SearchService,
    private snackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (this._dataSource == null) {
      this.dataSource = new MatTableDataSource();
    }

    this.navService.selectedOrg$.subscribe((org: any) => {
      if (org) {
        this.selectedOrganizationName = org.legalName;
      }
    });

    this.subscribeToFilterActiveEvent();
    this.initTableColumns();
  }

  ngAfterViewInit() {
    this._dataSource.sort = this.sort;
    this._dataSource.paginator = this.paginator;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }

    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  subscribeToFilterActiveEvent() {
    this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';
        if (event.value) {
          pillText = pillText + " : " + event.value;
        }
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
      this.cdr.detectChanges();
    });
  }

  updateSort() {
    if (!this._dataSource) {
      return;
    }

    /*INSIGHT: This disables the data source's internal sorting.
    * We are getting pre-sorted data from the API and do not need to sort it again.
    */
    this._dataSource.sortData = (data: any[], _: MatSort) => data;
    this._dataSource.sort = this.sort;
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  onSearchEvent(event: Event) {
    this.paginator.pageIndex = 0;
    this.pageNumber = 1;

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.searchTimeout = setTimeout(() => {
      const keyword = (event.target as HTMLInputElement).value;
      this.searchEvent.emit(keyword);
    }, this.searchTimeoutMs);
  }

  onDeleteKeyUp() {
    if (this.searchKeyword == '') {
      this.searchEvent.emit('');
    }
  }

  clearSearch() {
    this.searchService.resetQuery();
    this.searchKeyword = '';
    this.resetPaginator(1, true);
    this.searchEvent.emit('');
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageEvent.emit(event);
  }

  onSortEvent(event: any) {
    const { active: sortField, direction } = event;

    let sortOrder: SortOrderType;
    if (direction === 'asc') {
      sortOrder = 1;
    } else {
      sortOrder = -1;
    }

    this.sortEvent.emit(({ sortField, sortOrder }));
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/claims/detail/${row['id']}`]);
  }

  onActionButtonClick(event: MouseEvent, row: any): void {
    event.stopPropagation();
    this.actionClickedEvent.emit(row);
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.snackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  getColor(status: string) {
    if (this.getStatusColor) {
      return this.getStatusColor(status);
    } else {
      return '#d3d3d3'
    }
  }
}